import React, { memo, useCallback } from 'react';
import { ThemeProvider } from 'styled-components';
import { Router } from 'react-router';
import { useMount } from 'react-use';
import { hot } from 'react-hot-loader/root';
import { useDispatch, useSelector } from 'react-redux';

import theme from '@crm-framework/theme';
import { Loader } from '@crm-framework/loader';
import { getUser } from '@crm-framework/auth';

import { BaseLayout } from '@crm-package/layouts';
import { ErrorBoundary } from '@crm-package/error-boundary';

import { history } from './browser-history';
import { getAppState } from './selector';
import { appStartInit } from './reducer';
import { IAppProps } from './types';
import { Unavailable, Unauthorized } from './components';
import { LicenseExpiration } from './components/LicenseExpiration';

export const App: React.FC<IAppProps> = hot(
  memo(({ isLoginRedirect = true, children }) => {
    const dispatch = useDispatch();

    const [isAgreed, setIsAgreed] = React.useState(false);
    const { isUnavailable, isUnauthorized, isReady } = useSelector(getAppState);
    const { brandLicensingExpirationDate } = useSelector(getUser) ?? {};

    useMount(() => dispatch(appStartInit({ isLoginRedirect })));

    const onAgree = useCallback(() => {
      setIsAgreed(true);
    }, []);

    setInterval(() => {
      setIsAgreed(false);
    }, 1000 * 60 * 5);

    if (isUnavailable) {
      return <Unavailable />;
    }

    if (isUnauthorized) {
      return <Unauthorized />;
    }

    if (brandLicensingExpirationDate && !isAgreed) {
      return (
        <ThemeProvider theme={theme}>
          <LicenseExpiration
            expirationDate={brandLicensingExpirationDate}
            onAgree={onAgree}
          />
        </ThemeProvider>
      );
    }

    if (!isReady) {
      return <Loader fixed />;
    }

    return (
      <ThemeProvider theme={theme}>
        <Router history={history}>
          <BaseLayout>
            <ErrorBoundary>{children}</ErrorBoundary>
          </BaseLayout>
        </Router>
      </ThemeProvider>
    );
  })
);
